/**
 * What's new (owner, 2026-10-04: "when updates happen, there should be a 'what's new' modal that
 * comes up"). Each entry is one update, newest first: a title and one or two plain, terse lines
 * naming the new features, nothing more (owner: "What's new should not be in peon speak, it should
 * highlight new features, that's it, otherwise say minor fixes, bug fixes, etc. it's way too wordy.
 * It should not be the tutorial for the features"). Only a feature with a flag of its own gets an
 * entry (owner, 2026-10-05: "Anything without its own feature flag that you feel the need to add
 * to next up should be under the other subheading, not its own"). Fixes and polish are their own "Other" entry
 * with the one FIXES_LINE, not a line under a feature. No
 * flags, rules, decisions or how it was built in the copy.
 *
 * What's new follows the flags (owner, 2026-10-05: "It seems like the what's new is only being
 * updated for major native updates, OTA updates should also have what's new entries for new
 * features. Visibility dependent on global feature flags"). A feature behind a household flag has
 * its entry the day it ships, with `flag`, and the entry is visible only while that flag is on for
 * the device (the household's switch or the device's own, as the feature's own UI reads it). A
 * device remembers the set of ids it has been shown (not a high-water mark), so an entry hidden by
 * its flag today still shows the first open after the flag turns on, however many newer entries
 * showed meanwhile. A brand new device marks every id seen and shows nothing.
 *
 * Pure data and pure rules, read by both clients.
 */
import type { FlagKey } from "./types";

/** The flags an entry's visibility reads: each flag's effective value on this device (house or local). */
export type WhatsNewFlags = Partial<Record<FlagKey, boolean>>;

export interface WhatsNewEntry {
  /** Goes up by one per entry; what the device's seen set holds. */
  id: number;
  /** ISO date of the update. */
  date: string;
  /** The sheet's heading for this entry. */
  title: string;
  /** One or two lines naming the feature; the fixes note's one line. */
  lines: string[];
  /**
   * The update's fixes note: its own "Other" entry with "Minor fixes and improvements.", under no feature
   * (owner, 2026-10-04: "Any bug fixes and ux improvements etc should be in the patch notes as
   * such, not fully detailed", then "minor fixes and improvements should be its own note, not
   * under the feature subheading"). Never the Profile row's state line.
   */
  fixes?: boolean;
  /**
   * A deep link under the lines ("Try it out here"): a tap closes the sheet and goes to `route`,
   * a screen both clients know by that name (owner, 2026-10-04: "There can be deep links in the
   * what's new. In the character editor one, have a 'try it out here' clicking on it will navigate").
   */
  link?: { label: string; route: "peon" | "rewards" | "upgrades" | "open" | "done" | "trinkets" | "feedback" };
  /**
   * The household flag the feature sits behind: the entry shows only while it is on, so an entry
   * never names something the person cannot see. No flag, always shown.
   */
  flag?: FlagKey;
}

/** The fixes note: an "Other" section with the one line (owner, 2026-10-04: "It can live under an other subheading", "Or misc"). */
export const FIXES_TITLE = "Other";
export const FIXES_LINE = "Minor fixes and improvements.";

const TRY = "Try it out here";

export const WHATS_NEW: readonly WhatsNewEntry[] = [
  {
    // Owner, 2026-11-02: "Rather than big task it should be called a project and you can partially complete it, add a progress photo."
    id: 38,
    date: "2026-11-02",
    title: "Projects",
    lines: ["Big jobs are now projects. Add a progress photo as you go and the pot grows.", "Everyone who added progress shares the gold at the end."],
    flag: "projectsUI",
  },
  {
    // Owner, 2026-11-02: "Should be able to share task credits on things."
    id: 37,
    date: "2026-11-02",
    title: "Shared credit",
    lines: ["Did a job together? Pick who helped when you finish it, or share it after.", "Everyone gets a split of the gold, plus a little extra."],
    flag: "shareCreditUI",
  },
  {
    // Owner, 2026-10-29: "What are other ways to increase engagement?", picking themed weeks.
    id: 36,
    date: "2026-10-29",
    title: "Themed weeks",
    lines: ["Every week has a theme on the notice board. Chores that match it pay extra."],
    flag: "themeWeekUI",
  },
  {
    // Owner, 2026-10-29: "What are other ways to increase engagement?", picking the house goal of the week.
    id: 35,
    date: "2026-10-29",
    title: "House goal",
    lines: ["The whole house works toward one goal each week. Hit it and everyone gets wood."],
    flag: "houseGoalUI",
  },
  {
    // Owner, 2026-10-28: playing "Hello [name]!" when the app launches.
    id: 34,
    date: "2026-10-28",
    title: "Hello by name",
    lines: ["The peon greets you by name when you open the app."],
    flag: "helloUI",
  },
  {
    // Owner, 2026-10-28: "There need to be more ways which encourage people to do assigned chores", picking cheers.
    id: 33,
    date: "2026-10-28",
    title: "Cheers",
    lines: ["When someone finishes a job, it pops up so you can cheer them. Confetti included."],
    flag: "cheersUI",
  },
  {
    // Owner, 2026-10-28: "There need to be more ways which encourage people to do assigned chores".
    id: 32,
    date: "2026-10-28",
    title: "Daily reminder",
    lines: ["Once a day, at your usual hour, a notification names the tasks the wheel picked you for."],
    flag: "yoursPushUI",
  },
  {
    // Owner, 2026-10-23: "Should have a what's new entry for the suggestion feature", "Should also be feature flagged".
    id: 31,
    date: "2026-10-23",
    title: "Suggest something",
    lines: ["Send an idea or a bug, with up to 3 photos, from Profile under About."],
    // Owner, 2026-10-24: "The what's new entry should have a try it out link".
    link: { label: TRY, route: "feedback" },
    flag: "feedbackUI",
  },
  {
    id: 30,
    date: "2026-10-23",
    title: "Slop tally",
    lines: ["The notice board counts the days without Omie's slop. Anyone can reset it."],
    flag: "slopTallyUI",
  },
  {
    id: 29,
    date: "2026-10-20",
    title: "Photo notes",
    lines: ["Pin a photo to the notice board, with a caption if you like."],
    flag: "photoNotesUI",
  },
  {
    id: 28,
    date: "2026-10-20",
    title: "Speedrun note",
    lines: ["Hue's newest best time stays pinned to the notice board."],
    flag: "speedrunUI",
  },
  {
    id: 27,
    date: "2026-10-19",
    title: "Name tags",
    lines: ["Name a peon on the scroll, in a note or on a task to tag them in their colour. Type @ to pick one."],
    flag: "mentionsUI",
  },
  {
    id: 26,
    date: "2026-10-12",
    title: "Note hearts",
    lines: ["Heart a pinned note. Open it and tap the heart."],
    flag: "heartsUI",
  },
  {
    id: 25,
    date: "2026-10-11",
    title: "Late chore rush",
    lines: ["Late chores come as one gold rush, at your usual hour: double gold on all of them for an hour."],
    flag: "lateRushUI",
  },
  {
    id: 24,
    date: "2026-10-11",
    title: "Trinkets",
    lines: ["Chores turn up trinkets now and then. Turn each one around and hear its tune.", "The household's collection is in Profile."],
    link: { label: TRY, route: "trinkets" },
    flag: "trinketsUI",
  },
  {
    id: 23,
    date: "2026-10-09",
    title: "Give credit",
    lines: ["Someone else did the job? On a done task, Give credit hands it to them, gold and all.", "Quietly. Nobody gets a push."],
    flag: "giveCreditUI",
  },
  { id: 22, date: "2026-10-06", title: "Yours", lines: ["Tasks assigned to you or you're on, first on the board."], flag: "yoursUI" },
  { id: 21, date: "2026-10-05", title: "Task roulette", lines: ["Tasks nobody takes for three days are spun to a peon."], flag: "rouletteUI" },
  {
    id: 20,
    date: "2026-10-05",
    title: "Household wallpaper",
    lines: ["Chip in wood for a new wall behind every screen."],
    link: { label: TRY, route: "upgrades" },
    flag: "wallpaperUI",
  },
  {
    id: 19,
    date: "2026-10-05",
    title: "Wagers",
    lines: ["Stake gold on who finishes their next chore first."],
    link: { label: TRY, route: "rewards" },
    flag: "wagerUI",
  },
  { id: 18, date: "2026-10-05", title: "Gold rush pick", lines: ["The day's lowest earner picks tomorrow's gold rush."], flag: "rushPickUI" },
  { id: 17, date: "2026-10-05", title: "Luxury auction", lines: ["An untaken luxury task pays more each day."], flag: "auctionUI" },
  { id: 16, date: "2026-10-05", title: "Streaks", lines: ["Two days in a row, then more gold from day three."], flag: "streakUI" },
  { id: 15, date: "2026-10-05", title: "Household QR", lines: ["Show an invite as a QR code to scan."] },
  { id: 14, date: "2026-10-05", title: "Peon moods", lines: ["Your peon reacts to the board, and naps when it's empty."], flag: "peonMoodsUI" },
  { id: 13, date: "2026-10-05", title: "Photo compare", lines: ["Slide between the before and after of a done task."], flag: "compareUI" },
  {
    id: 12,
    date: "2026-10-05",
    title: "Done gallery",
    lines: ["See the after photos in a grid."],
    link: { label: TRY, route: "done" },
    flag: "galleryUI",
  },
  {
    id: 11,
    date: "2026-10-05",
    title: "Weekly scroll",
    lines: ["Write appreciations and accomplishments: 10 gold each, up to five a week."],
    link: { label: TRY, route: "open" },
    flag: "weeklyUI",
  },
  { id: 10, date: "2026-10-04", title: "Nudges", lines: ["Nudge a peon about a task, once a day."], flag: "nudgeUI" },
  { id: 9, date: "2026-10-04", title: "Me do it", lines: ["Claim a task for an hour so nobody doubles up."], flag: "claimUI" },
  { id: 8, date: "2026-10-04", title: "Ranks", lines: ["A rank from the gold you've earned, under your name."], flag: "ranksUI" },
  { id: 7, date: "2026-10-04", title: "Badges", lines: ["Badges for little feats: see them in Your peon, wear one."], flag: "achievementsUI" },
  {
    id: 6,
    date: "2026-10-04",
    title: "Purse",
    lines: ["See where your gold went."],
    link: { label: TRY, route: "rewards" },
    flag: "purseUI",
  },
  { id: 5, date: "2026-10-05", title: FIXES_TITLE, lines: [FIXES_LINE], fixes: true },
  { id: 4, date: "2026-10-04", title: FIXES_TITLE, lines: [FIXES_LINE], fixes: true },
  {
    id: 3,
    date: "2026-10-04",
    title: "Character editor",
    lines: ["Hairstyles, beards and accessories for your peon."],
    link: { label: TRY, route: "peon" },
  },
  {
    id: 2,
    date: "2026-10-04",
    title: "Peon voice notifications",
    lines: ["Notifications play the peon's lines.", "The tutorial plays as you go."],
  },
  {
    id: 1,
    date: "2026-10-04",
    title: "Timed notes",
    lines: ["A note can ring at a set time, for anyone you choose."],
  },
];

/** The lines the sheet shows under an entry's title. */
export function whatsNewLines(entry: WhatsNewEntry): string[] {
  return entry.lines;
}

/**
 * The sheet's order: the features newest first, then one "Other" fixes note last. Several updates'
 * fixes notes collapse to the newest, so "Minor fixes and improvements." reads once.
 */
export function orderWhatsNew(entries: readonly WhatsNewEntry[]): WhatsNewEntry[] {
  const sorted = [...entries].sort((a, b) => Number(!!a.fixes) - Number(!!b.fixes) || b.id - a.id);
  const firstFix = sorted.findIndex((e) => e.fixes);
  return sorted.filter((e, i) => !e.fixes || i === firstFix);
}

/** Whether an entry shows on this device: no flag, always; a flag, only while it is on. */
export function whatsNewVisible(entry: WhatsNewEntry, flags: WhatsNewFlags): boolean {
  return !entry.flag || flags[entry.flag] === true;
}

/** The entries this device may see now: every unflagged one, and each flagged one whose flag is on. */
export function visibleWhatsNew(entries: readonly WhatsNewEntry[], flags: WhatsNewFlags): WhatsNewEntry[] {
  return entries.filter((e) => whatsNewVisible(e, flags));
}

/**
 * The entries an open shows: visible, not in the device's seen set, in the sheet's order (features
 * newest first, one Other note last). A brand new device (`seen` null) shows nothing.
 */
export function unseenWhatsNew(seen: ReadonlySet<number> | null, flags: WhatsNewFlags, entries: readonly WhatsNewEntry[] = WHATS_NEW): WhatsNewEntry[] {
  if (seen === null) return [];
  return orderWhatsNew(visibleWhatsNew(entries, flags).filter((e) => !seen.has(e.id)));
}

/** Every visible entry, in the sheet's order: what About in Profile shows (shared/account-menu.ts). */
export function allWhatsNew(flags: WhatsNewFlags, entries: readonly WhatsNewEntry[] = WHATS_NEW): WhatsNewEntry[] {
  return orderWhatsNew(visibleWhatsNew(entries, flags));
}

/** The latest entry's id: what the old bookmark key still gets, for one release (see readSeenIds). */
export function latestWhatsNewId(): number {
  return WHATS_NEW.reduce((top, e) => Math.max(top, e.id), 0);
}

/** Every entry's id: what a brand new device marks seen on its first open, visible or not. */
export function allWhatsNewIds(entries: readonly WhatsNewEntry[] = WHATS_NEW): number[] {
  return entries.map((e) => e.id);
}

// ---------- The device's seen set ----------

/** The ids this device has been shown, as a JSON array of numbers. Both clients, same key. */
export const WHATS_NEW_SEEN_IDS_KEY = "op.whatsNewSeenIds";
/**
 * The old bookmark: the highest id shown, as a decimal string. Read once to migrate, and still
 * written (the highest seen id) for one release after 2026-10-05, so a device that goes back to a
 * build before the seen set still shows only what is newer than it. Drop the write after that.
 */
export const WHATS_NEW_BOOKMARK_KEY = "op.whatsNewSeen";

/** A stored seen set, or null when there is none or it is unreadable. */
export function parseSeenIds(raw: string | null): Set<number> | null {
  if (raw === null) return null;
  try {
    const list = JSON.parse(raw) as unknown;
    if (!Array.isArray(list) || !list.every((n) => Number.isInteger(n) && n >= 0)) return null;
    return new Set(list as number[]);
  } catch {
    return null;
  }
}

/** The old bookmark as a seen set: every id at or below it counts as seen; null when it is missing or unreadable. */
export function seenFromBookmark(raw: string | null, entries: readonly WhatsNewEntry[] = WHATS_NEW): Set<number> | null {
  if (raw === null || !/^\d+$/.test(raw.trim())) return null;
  const top = Number(raw);
  return new Set(entries.filter((e) => e.id <= top).map((e) => e.id));
}

/**
 * The device's seen set from what it stores: the seen ids when they are there, else the old
 * bookmark migrated (every id at or below it), else null (no record: brand new, or in use before
 * What's new existed; the client decides which).
 */
export function readSeenIds(rawIds: string | null, rawBookmark: string | null): Set<number> | null {
  return parseSeenIds(rawIds) ?? seenFromBookmark(rawBookmark);
}

/** The seen set as stored: a sorted JSON array. */
export function serializeSeenIds(seen: ReadonlySet<number>): string {
  return JSON.stringify([...seen].sort((a, b) => a - b));
}

/** The old bookmark's value for a seen set: its highest id (0 for none). */
export function bookmarkForSeen(seen: ReadonlySet<number>): string {
  return String([...seen].reduce((top, id) => Math.max(top, id), 0));
}
